import { addMonths, differenceInCalendarDays } from 'date-fns'

import arriereBackoffice, {
  AnalystHomeNewAssetsWindow,
  RevenueManagementAutomationCleaningMarginDateBasis,
  RevenueManagementAutomationCleaningMarginSegment,
  RevenueManagementAutomationSortOrder,
  TicketStatus,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { formatDate, SHORT_ISO_DATE_FORMAT } from '@avantstay/backoffice-core'

export const getAnalystHomeScope = async () =>
  arriereBackoffice.queries.analystHomeScope({
    __alias: 'getAnalystHomeScope',
    analysts: { analystId: true, name: true, email: true, homeCount: true, marketIds: true },
    markets: { marketId: true, marketName: true },
  })

// Service facade for the Analyst Home (Revenue Home) module.
//
// AnalystHome reuses queries already exposed by the RevenueAgents and
// RevenueTickets modules. Helpers in this file collect the AnalystHome-specific
// shapes (e.g. summed counts) so the rest of the module only imports from
// here. When a dedicated `/api/revenue/home/*` endpoint ships, swap the
// implementation here without touching the store or the UI.
//
// Add new helpers only when AnalystHome actually consumes them — this file is
// intentionally a thin surface, not a re-export catalog.

const OPEN_TICKET_STATUSES: TicketStatus[] = [TicketStatus.open, TicketStatus.inProgress]

export interface OpenTicketsCountFilter {
  marketIds?: string[]
  assignee?: string
}

// Counts tickets currently open or in progress. We call the underlying
// `tickets` query directly here (instead of going through `getRevenueTickets`)
// so we can pass both statuses in a single multi-status filter — the BE `status`
// filter is an array and ORs the values, so one round-trip returns the combined
// total. Going through the shared helper would require fanning out into parallel
// calls per refetch, which (combined with MobX recomputes when the scope
// changes) was the main source of getRevenueTickets request volume on this page.
//
// Page size 1 keeps the response payload minimal — we only care about `total`.
export async function getOpenTicketsCount(filter: OpenTicketsCountFilter = {}): Promise<number> {
  // Conditionally spread the scope filters so the BE receives a clean filter
  // payload — for the "Everyone" view we pass neither `assigneesIds` nor
  // `marketIds`, which lets the backend return a global open-ticket count.
  const result = await arriereBackoffice.queries.tickets({
    __alias: 'getOpenTicketsCount',
    __args: {
      filter: {
        status: OPEN_TICKET_STATUSES,
        ...(filter.assignee ? { assigneesIds: [filter.assignee] } : {}),
        ...(filter.marketIds?.length ? { marketIds: filter.marketIds } : {}),
        page: 1,
        pageSize: 1,
      },
    },
    total: true,
  })

  return result.total ?? 0
}

export interface PacingSeriesFilter {
  marketIds?: string[]
  months?: number
}

export interface PacingSeriesPoint {
  month: string
  year: number
  deltaPercent: number
  hasData: boolean
}

export interface PacingSeriesData {
  headlineDeltaPercent: number
  series: PacingSeriesPoint[]
  lastUpdated?: string
}

// Revenue pacing for the AnalystHome top-metrics "Revenue Pacing" card: a
// headline delta vs underwriting target plus the month-by-month series the
// card chart renders. Scoped server-side by `marketIds`, so the page hands the
// dedicated `analystHomePacingSeries` query its already-resolved market scope
// and renders the result directly — no client-side aggregation.
export async function getPacingSeries(filter: PacingSeriesFilter = {}): Promise<PacingSeriesData> {
  const result = await arriereBackoffice.queries.analystHomePacingSeries({
    __alias: 'getPacingSeries',
    __args: {
      input: {
        marketIds: filter.marketIds ?? [],
        ...(filter.months ? { months: filter.months } : {}),
      },
    },
    headlineDeltaPercent: true,
    lastUpdated: true,
    series: {
      month: true,
      year: true,
      deltaPercent: true,
      hasData: true,
    },
  })

  return {
    headlineDeltaPercent: result.headlineDeltaPercent ?? 0,
    lastUpdated: result.lastUpdated,
    series: (result.series ?? []).map(point => ({
      month: point.month,
      year: point.year,
      deltaPercent: point.deltaPercent ?? 0,
      hasData: point.hasData,
    })),
  }
}

interface GetMarketsOverviewParams {
  marketIds: string[]
  analystId?: string
  startDate: Date
  endDate: Date
}

export const getMarketsOverview = async ({
  marketIds,
  analystId,
  startDate,
  endDate,
}: GetMarketsOverviewParams) => {
  const result = await arriereBackoffice.queries.analystHomeMarketsOverview({
    __alias: 'getMarketsOverview',
    __args: {
      input: {
        marketIds,
        analystId,
        startDate: formatDate(startDate, SHORT_ISO_DATE_FORMAT),
        endDate: formatDate(endDate, SHORT_ISO_DATE_FORMAT),
      },
    },
    markets: {
      marketId: true,
      marketName: true,
      propertiesCount: true,
      pacingNext3m: true,
      sameStoreRevpauNext3m: true,
      sameStoreRevpauPast3m: true,
      criticalHomes: { count: true, total: true, share: true },
      underperformingHomes: { count: true, total: true, share: true },
      pendingDecisions: true,
      openTickets: true,
      discountRequests: true,
      openings: true,
    },
  })

  return result.markets
}

interface GetHolidayIndicatorsParams {
  marketIds: string[]
  horizonMonths: number
}

export const getHolidayIndicators = async ({
  marketIds,
  horizonMonths,
}: GetHolidayIndicatorsParams) => {
  const today = new Date()
  const result = await arriereBackoffice.queries.analystHomeHolidayIndicators({
    __alias: 'getHolidayIndicators',
    __args: {
      input: {
        marketIds,
        horizonDays: differenceInCalendarDays(addMonths(today, horizonMonths), today),
      },
    },
    holidays: { seasonName: true, seasonType: true, startDate: true, endDate: true },
    cells: {
      marketId: true,
      seasonName: true,
      occupancy: true,
      marketOccupancy: true,
      pickupRevenue7d: true,
      revenueVsTarget: true,
      revparVsStly: true,
    },
  })

  return {
    holidays: result.holidays.map(holiday => ({
      ...holiday,
      startDate: formatDate(holiday.startDate, SHORT_ISO_DATE_FORMAT),
      endDate: formatDate(holiday.endDate, SHORT_ISO_DATE_FORMAT),
    })),
    cells: result.cells,
  }
}

interface GetAnalystHomeBookingWindowParams {
  marketIds: string[]
  analystId?: string
  horizonDays: number
}

export const getAnalystHomeBookingWindow = async ({
  marketIds,
  analystId,
  horizonDays,
}: GetAnalystHomeBookingWindowParams) => {
  const result = await arriereBackoffice.queries.analystHomeBookingWindow({
    __alias: 'getAnalystHomeBookingWindow',
    __args: { input: { marketIds, analystId, horizonDays } },
    months: true,
    curveAsOf: true,
    markets: {
      rmMarket: true,
      name: true,
      peakMonth: true,
      months: {
        month: true,
        annualRevenueShare: true,
        expectedRevenue: true,
        target: true,
        pacing: true,
        sameStore: {
          netRevenueChange: true,
          netRevenueDelta: true,
          adrChange: true,
          occupancyChange: true,
          revpauChange: true,
        },
      },
    },
  })

  return {
    months: result.months.map(month => formatDate(month, SHORT_ISO_DATE_FORMAT)),
    curveAsOf:
      result.curveAsOf != null ? formatDate(result.curveAsOf, SHORT_ISO_DATE_FORMAT) : null,
    markets: result.markets.map(market => ({
      ...market,
      peakMonth: formatDate(market.peakMonth, SHORT_ISO_DATE_FORMAT),
      months: market.months.map(month => ({
        ...month,
        month: formatDate(month.month, SHORT_ISO_DATE_FORMAT),
      })),
    })),
  }
}

interface GetAnalystHomeKpisParams {
  marketIds: string[]
  analystId?: string
  horizonDays?: number
}

export const getAnalystHomeKpis = async ({
  marketIds,
  analystId,
  horizonDays,
}: GetAnalystHomeKpisParams) => {
  const result = await arriereBackoffice.queries.analystHomeKpis({
    __alias: 'getAnalystHomeKpis',
    __args: { input: { marketIds, analystId, horizonDays } },
    horizonDays: true,
    otb: { fromMonth: true, toMonth: true, netRevenue: true, netRevenueVsStly: true },
    pacing: { fromMonth: true, toMonth: true, pacing: true, vsPlanPts: true },
    sameStoreRevpau: {
      fromMonth: true,
      toMonth: true,
      revpau: true,
      vsStly: true,
      sameStoreHomes: true,
      homes: true,
    },
    bookings7d: {
      bookingValue: true,
      bookingValueChange: true,
      bookings: true,
      bookingsChange: true,
    },
    criticalProperties: {
      listPublished: true,
      planDate: true,
      homes: true,
      needActionToday: true,
      definition: true,
    },
  })
  const { planDate } = result.criticalProperties

  return {
    ...result,
    criticalProperties: {
      ...result.criticalProperties,
      planDate: planDate != null ? formatDate(planDate, SHORT_ISO_DATE_FORMAT) : null,
    },
  }
}

interface GetAnalystHomeRevparParams {
  marketIds: string[]
  analystId?: string
}

export const getAnalystHomeRevpar = async ({
  marketIds,
  analystId,
}: GetAnalystHomeRevparParams) => {
  const result = await arriereBackoffice.queries.analystHomeRevpar({
    __alias: 'getAnalystHomeRevpar',
    __args: { input: { marketIds, analystId } },
    months: {
      month: true,
      revpar: true,
      stlyRevpar: true,
      revparVsStly: true,
      occupancy: true,
      adr: true,
      homes: true,
      sameHomes: true,
      sameHomesRevparVsStly: true,
    },
    weeks: {
      isoWeek: true,
      weekStart: true,
      weekEnd: true,
      occupancy: true,
      adr: true,
      revpar: true,
      lyRevpar: true,
      revparVsLy: true,
      homes: true,
      sameHomes: true,
      sameHomesRevparVsLy: true,
    },
  })

  return {
    months: result.months.map(revparMonth => ({
      ...revparMonth,
      month: formatDate(revparMonth.month, SHORT_ISO_DATE_FORMAT),
    })),
    weeks: result.weeks.map(week => ({
      ...week,
      weekStart: formatDate(week.weekStart, SHORT_ISO_DATE_FORMAT),
      weekEnd: formatDate(week.weekEnd, SHORT_ISO_DATE_FORMAT),
    })),
  }
}

interface GetAnalystHomeMonthlySeriesParams {
  marketIds: string[]
  analystId?: string
}

export const getAnalystHomeMonthlySeries = async ({
  marketIds,
  analystId,
}: GetAnalystHomeMonthlySeriesParams) => {
  const result = await arriereBackoffice.queries.analystHomeMonthlySeries({
    __alias: 'getAnalystHomeMonthlySeries',
    __args: { input: { marketIds, analystId } },
    months: { month: true, isPast: true, otb: true, target: true, stly: true, ly: true },
  })

  return result.months.map(seriesMonth => ({
    ...seriesMonth,
    month: formatDate(seriesMonth.month, SHORT_ISO_DATE_FORMAT),
  }))
}

export interface PacingCriticalProperty {
  propertyId: string
  propertyName: string
  market: string
  pacingDelta: number
}

export interface PacingCriticalPropertiesData {
  properties: PacingCriticalProperty[]
  lastUpdated?: string
}

export interface PacingCriticalPropertiesFilter {
  marketIds?: string[]
  startDate: Date | string
  endDate: Date | string
}

// Properties pacing critically below target for the AnalystHome "Properties
// pacing critical" card. Backed by the dedicated `analystHomePropertiesPacing
// Critical` query, scoped server-side by `marketIds` over the
// `[startDate, endDate]` analysis window. Returns the full critical set; the
// caller derives the headline count from its length.
export async function getPropertiesPacingCritical(
  filter: PacingCriticalPropertiesFilter,
): Promise<PacingCriticalPropertiesData> {
  const result = await arriereBackoffice.queries.analystHomePropertiesPacingCritical({
    __alias: 'getPropertiesPacingCritical',
    __args: {
      input: {
        marketIds: filter.marketIds ?? [],
        startDate: formatDate(filter.startDate, SHORT_ISO_DATE_FORMAT),
        endDate: formatDate(filter.endDate, SHORT_ISO_DATE_FORMAT),
      },
    },
    lastUpdated: true,
    properties: {
      propertyId: true,
      propertyName: true,
      market: true,
      pacingDelta: true,
    },
  })

  return {
    lastUpdated: result.lastUpdated,
    properties: (result.properties ?? []).map(property => ({
      propertyId: property.propertyId,
      propertyName: property.propertyName ?? '',
      market: property.market ?? '',
      pacingDelta: property.pacingDelta ?? 0,
    })),
  }
}

export interface AnalystActivityData {
  decisionsReviewed: { value: number; wowDelta: number }
  pricingToolReviews: { value: number; wowDelta: number }
  approvalRate: number
  portfolioApprovalRate: number
  backlogAgeDays: number
  lastUpdated?: string
}

// "Your week" reviewer stats for a single analyst, via the dedicated
// `analystActivity` query. Weekly counts come back as this-week / last-week, so
// we surface this-week as the value and the week-over-week delta alongside it
// (falling back to the computed diff when the BE doesn't send one).
export async function getAnalystActivity(analystId: string): Promise<AnalystActivityData> {
  const result = await arriereBackoffice.queries.analystActivity({
    __alias: 'getAnalystActivity',
    __args: { analystId },
    decisionsReviewed: { thisWeek: true, lastWeek: true, wowDelta: true },
    pricingToolReviews: { thisWeek: true, lastWeek: true, wowDelta: true },
    decisionApprovalRate: true,
    portfolioApprovalRate: true,
    backlogAgeDays: true,
    lastUpdated: true,
  })

  const toWeekly = (weekly: { thisWeek: number; lastWeek: number; wowDelta?: number }) => ({
    value: weekly.thisWeek,
    wowDelta: weekly.wowDelta ?? weekly.thisWeek - weekly.lastWeek,
  })

  return {
    decisionsReviewed: toWeekly(result.decisionsReviewed),
    pricingToolReviews: toWeekly(result.pricingToolReviews),
    approvalRate: result.decisionApprovalRate ?? 0,
    portfolioApprovalRate: result.portfolioApprovalRate ?? 0,
    backlogAgeDays: result.backlogAgeDays ?? 0,
    lastUpdated: result.lastUpdated,
  }
}

// Flat per-item context the BE composes for the triage feed. Fields are
// populated per category, so consumers read whichever are present.
export interface TriageFeedContext {
  type: string
  percentVsTargetNext30Days?: number
  bookingsLast7Days?: number
  openTickets?: number
  highestPriority?: string
  oldestOpenedAt?: string
  pendingDecisions?: number
  oldestPendingAt?: string
  flagContent?: string
  highestSeverity?: string
}

export interface TriageFeedItem {
  id: string
  priority: string
  category: string
  propertyId?: string
  propertyName?: string
  market: string
  titleKey: string
  context?: TriageFeedContext
  hasFlags?: { count: number; highestSeverity?: string }
  thumbnailUrl?: string
}

export interface TriageFeedData {
  items: TriageFeedItem[]
  backlogAge: { days: number; targetDays: number }
  lastUpdated?: string
}

// "Today's focus" triage feed for the scoped markets, fully composed by the BE
// (`analystHomeTriageFeed`) — replaces the former client-side rule engine.
export async function getTriageFeed(marketIds: string[]): Promise<TriageFeedData> {
  const result = await arriereBackoffice.queries.analystHomeTriageFeed({
    __alias: 'getTriageFeed',
    __args: { input: { marketIds } },
    lastUpdated: true,
    backlogAge: { days: true, targetDays: true },
    items: {
      id: true,
      priority: true,
      category: true,
      propertyId: true,
      propertyName: true,
      market: true,
      titleKey: true,
      contextDescriptor: {
        type: true,
        percentVsTargetNext30Days: true,
        bookingsLast7Days: true,
        openTickets: true,
        highestPriority: true,
        oldestOpenedAt: true,
        pendingDecisions: true,
        oldestPendingAt: true,
        flagContent: true,
        highestSeverity: true,
      },
      hasFlags: { count: true, highestSeverity: true },
      home: { thumbnail: { url: true } },
    },
  })

  return {
    lastUpdated: result.lastUpdated,
    backlogAge: {
      days: result.backlogAge?.days ?? 0,
      targetDays: result.backlogAge?.targetDays ?? 0,
    },
    items: (result.items ?? []).map(item => ({
      id: `${item.id}`,
      priority: item.priority,
      category: item.category,
      propertyId: item.propertyId,
      propertyName: item.propertyName,
      market: item.market ?? '',
      titleKey: item.titleKey,
      context: item.contextDescriptor
        ? {
            type: item.contextDescriptor.type,
            percentVsTargetNext30Days: item.contextDescriptor.percentVsTargetNext30Days,
            bookingsLast7Days: item.contextDescriptor.bookingsLast7Days,
            openTickets: item.contextDescriptor.openTickets,
            highestPriority: item.contextDescriptor.highestPriority,
            oldestOpenedAt: item.contextDescriptor.oldestOpenedAt,
            pendingDecisions: item.contextDescriptor.pendingDecisions,
            oldestPendingAt: item.contextDescriptor.oldestPendingAt,
            flagContent: item.contextDescriptor.flagContent,
            highestSeverity: item.contextDescriptor.highestSeverity,
          }
        : undefined,
      hasFlags: item.hasFlags
        ? { count: item.hasFlags.count, highestSeverity: item.hasFlags.highestSeverity }
        : undefined,
      thumbnailUrl: item.home?.thumbnail?.url,
    })),
  }
}

export type CleaningMarginResponse = Awaited<ReturnType<typeof getCleaningMargin>>
export type RealizedWeek = CleaningMarginResponse['realized'][number]
export type ProjectedDay = CleaningMarginResponse['projected'][number]
export type DriftDay = CleaningMarginResponse['drift'][number]

export async function getCleaningMargin(
  excludeSegments: RevenueManagementAutomationCleaningMarginSegment[] = [],
  dateBasis = RevenueManagementAutomationCleaningMarginDateBasis.checkIn,
  excludeWorkflowIds: string[] = [],
) {
  const { data, errors } = await arriereBackoffice.queries.revenueManagement.raw({
    __alias: 'revenueHomeCleaningMargin',
    cleaningMarginBaseline: true,
    cleaningMarginProjectedDaily: {
      __args: { market: 'ALL', population: 'portfolio', excludeSegments },
      snapshotDate: true,
      market: true,
      population: true,
      runId: true,
      ruleVersion: true,
      homesTotal: true,
      homesWeighted: true,
      homesExcluded: true,
      homesMissingFinancials: true,
      homesMissingTurnover: true,
      ltmCleansSum: true,
      feeWeightedSum: true,
      costWeightedSum: true,
      projectedMarginCleanWeighted: true,
      avgHomeMarginUnweighted: true,
      avgHomeTargetMarginUnweighted: true,
      computedAt: true,
    },
    cleaningMarginInputDriftDaily: {
      snapshotDate: true,
      runId: true,
      prevRunId: true,
      ruleVersion: true,
      prevRuleVersion: true,
      ruleVersionChanged: true,
      homesCompared: true,
      portfolioEntered: true,
      portfolioLeft: true,
      exclusionEntered: true,
      exclusionLeft: true,
      sentimentChangedHomes: true,
      costChangedHomes: true,
      costChangeMeanDelta: true,
      computedAt: true,
    },
    cleaningMarginRealizedWeekly: {
      __args: {
        population: 'portfolio',
        includePartialWeeks: true,
        excludeSegments,
        dateBasis,
        excludeWorkflowIds,
      },
      dateBasis: true,
      onBooksMarginBookingWeighted: true,
      bookedMarginBookings: true,
      bookedFeeSum: true,
      bookedFeeCostDiffSum: true,
      excludedWorkflowHomes: true,
      isoYear: true,
      isoWeek: true,
      market: true,
      population: true,
      populationSize: true,
      bookings: true,
      checkedInBookings: true,
      bookedBookings: true,
      realizedMarginBookingWeighted: true,
      isPartialWeek: true,
      feeSum: true,
      feeCostDiffSum: true,
      computedAt: true,
    },
  })

  if (errors?.length) throw new Error(errors[0].message)
  if (
    !data?.cleaningMarginRealizedWeekly ||
    !data.cleaningMarginProjectedDaily ||
    !data.cleaningMarginInputDriftDaily
  ) {
    throw new Error('Cleaning margin measurements are unavailable.')
  }

  const realized = data.cleaningMarginRealizedWeekly.map(row => {
    const {
      isoYear,
      isoWeek,
      market,
      population,
      populationSize,
      bookings,
      checkedInBookings,
      bookedBookings,
      isPartialWeek,
      feeSum,
      feeCostDiffSum,
      computedAt,
    } = row
    // The generated client marks backend-required fields as optional.
    if (
      isoYear == null ||
      isoWeek == null ||
      market == null ||
      population == null ||
      populationSize == null ||
      bookings == null ||
      checkedInBookings == null ||
      bookedBookings == null ||
      isPartialWeek == null ||
      feeSum == null ||
      feeCostDiffSum == null ||
      computedAt == null
    ) {
      throw new Error('Cleaning margin measurements are incomplete.')
    }
    return {
      isoYear,
      isoWeek,
      market,
      population,
      populationSize,
      bookings,
      checkedInBookings,
      bookedBookings,
      isPartialWeek,
      feeSum,
      feeCostDiffSum,
      computedAt,
      realizedMarginBookingWeighted: row.realizedMarginBookingWeighted ?? null,
      onBooksMarginBookingWeighted: row.onBooksMarginBookingWeighted ?? null,
      dateBasis: row.dateBasis,
      bookedMarginBookings: row.bookedMarginBookings,
      bookedFeeSum: row.bookedFeeSum,
      bookedFeeCostDiffSum: row.bookedFeeCostDiffSum,
      excludedWorkflowHomes: row.excludedWorkflowHomes,
    }
  })

  return {
    cleaningMarginBaseline: data.cleaningMarginBaseline ?? null,
    projected: data.cleaningMarginProjectedDaily,
    drift: data.cleaningMarginInputDriftDaily,
    realized,
  }
}

export async function getCleaningMarginWorkflows() {
  const { data, errors } = await arriereBackoffice.queries.revenueManagement.raw({
    __alias: 'revenueHomeCleaningMarginWorkflows',
    cleaningMarginWorkflows: {
      id: true,
      name: true,
      market: true,
      status: true,
      changedHomes: true,
      computedAt: true,
    },
  })
  if (errors?.length) throw new Error(errors[0].message)
  if (!data?.cleaningMarginWorkflows) throw new Error('Cleaning margin workflows are unavailable.')
  return data.cleaningMarginWorkflows.flatMap(row =>
    row.id
      ? [
          {
            ...row,
            id: row.id,
            market: row.market ?? row.name ?? row.id,
            changedHomes: row.changedHomes ?? 0,
          },
        ]
      : [],
  )
}

export type CleaningMarginWorkflow = Awaited<ReturnType<typeof getCleaningMarginWorkflows>>[number]

interface GetNewAssetHomesParams {
  marketIds: string[]
  window: AnalystHomeNewAssetsWindow
}

export const getNewAssetHomes = async ({ marketIds, window }: GetNewAssetHomesParams) => {
  const result = await arriereBackoffice.queries.analystHomeNewAssets({
    __alias: 'getNewAssetHomes',
    __args: { input: { marketIds, window } },
    homes: {
      propertyId: true,
      propertyName: true,
      marketName: true,
      thumbnailUrl: true,
      rampingPeriod: { startDate: true, endDate: true },
      liveDays: true,
      y2Underwriting: true,
      revenue: true,
      revenueTargetAmount: true,
      cumulativeRevenueTargetAmount: true,
      occupancy: true,
      bookings: { bookingHash: true, bookingDate: true },
    },
  })

  return result.homes
}

interface GetAnalystHomeTodayFocusParams {
  marketIds: string[]
  analystId?: string
}

export const getAnalystHomeTodayFocus = async ({
  marketIds,
  analystId,
}: GetAnalystHomeTodayFocusParams) =>
  arriereBackoffice.queries.analystHomeTodayFocus({
    __alias: 'getAnalystHomeTodayFocus',
    __args: { input: { marketIds, analystId } },
    listPublished: true,
    planDate: true,
    publishedAt: true,
    reservationFlags: {
      homes: true,
      reservations: true,
      byReason: { key: true, label: true, count: true },
      doneHomes: true,
    },
    newAssetReviews: {
      homes: true,
      byBucket: { key: true, label: true, count: true },
      doneHomes: true,
      pendingPricing: true,
    },
    ownerTickets: {
      total: true,
      byStatus: { key: true, label: true, count: true },
      oldestCreatedAt: true,
    },
    criticalPacing: { homes: true, needActionToday: true, definition: true },
  })

interface GetAnalystHomeCleaningMarginParams {
  marketIds: string[]
  analystId?: string
}

export const getAnalystHomeCleaningMargin = async ({
  marketIds,
  analystId,
}: GetAnalystHomeCleaningMarginParams) => {
  const result = await arriereBackoffice.queries.analystHomeCleaningMargin({
    __alias: 'getAnalystHomeCleaningMargin',
    __args: { input: { marketIds, analystId } },
    fromDate: true,
    toDate: true,
    lastUpdated: true,
    total: {
      margin: true,
      target: true,
      targetHomesMargin: true,
      attainment: true,
      signal: true,
      targetCoverage: true,
      stays: true,
      homes: true,
    },
  })

  return {
    ...result,
    fromDate: formatDate(result.fromDate, SHORT_ISO_DATE_FORMAT),
    toDate: formatDate(result.toDate, SHORT_ISO_DATE_FORMAT),
  }
}

interface GetAnalystHomeNegativeCleaningMarginHomesParams {
  marketIds: string[]
  analystId?: string
}

export const getAnalystHomeNegativeCleaningMarginHomes = async ({
  marketIds,
  analystId,
}: GetAnalystHomeNegativeCleaningMarginHomesParams) => {
  const result = await arriereBackoffice.queries.analystHomeNegativeCleaningMarginHomes({
    __alias: 'getAnalystHomeNegativeCleaningMarginHomes',
    __args: { input: { marketIds, analystId } },
    runDate: true,
    fromDate: true,
    toDate: true,
    homes: {
      propertyId: true,
      propertyName: true,
      thumbnailUrl: true,
      marketId: true,
      currentTotalFee: true,
      cleaningCost: true,
      currentMargin: true,
      targetMargin: true,
      proposedTotalFee: true,
      proposedMargin: true,
      action: true,
      isRunnable: true,
      notRunnableReasons: true,
      stays: true,
    },
  })

  return {
    ...result,
    runDate: result.runDate != null ? formatDate(result.runDate, SHORT_ISO_DATE_FORMAT) : null,
    fromDate: formatDate(result.fromDate, SHORT_ISO_DATE_FORMAT),
    toDate: formatDate(result.toDate, SHORT_ISO_DATE_FORMAT),
  }
}

interface GetMarketDetailRevenueTotalsParams {
  marketId: string
  analystId?: string
  fromMonth: Date
  toMonth: Date
}

export const getMarketDetailRevenueTotals = async ({
  marketId,
  analystId,
  fromMonth,
  toMonth,
}: GetMarketDetailRevenueTotalsParams) => {
  const result = await arriereBackoffice.queries.analystHomeMonthlySeries({
    __alias: 'getMarketDetailRevenueTotals',
    __args: {
      input: {
        marketIds: [marketId],
        analystId,
        fromMonth: formatDate(fromMonth, SHORT_ISO_DATE_FORMAT),
        toMonth: formatDate(toMonth, SHORT_ISO_DATE_FORMAT),
      },
    },
    totals: { otb: true, target: true, targetComplete: true, stly: true, ly: true },
  })

  return result.totals
}

interface GetMarketDetailWeeksParams {
  marketId: string
  analystId?: string
}

export const getMarketDetailWeeks = async ({ marketId, analystId }: GetMarketDetailWeeksParams) => {
  const result = await arriereBackoffice.queries.analystHomeRevpar({
    __alias: 'getMarketDetailWeeks',
    __args: { input: { marketIds: [marketId], analystId } },
    weeks: {
      isoWeek: true,
      weekStart: true,
      weekEnd: true,
      adr: true,
      occupancy: true,
      revpar: true,
      lyRevpar: true,
      revparVsLy: true,
      marketOccupancy: true,
      bookings: true,
      revenueVsTarget: true,
    },
  })

  return result.weeks.map(week => ({
    ...week,
    weekStart: formatDate(week.weekStart, SHORT_ISO_DATE_FORMAT),
    weekEnd: formatDate(week.weekEnd, SHORT_ISO_DATE_FORMAT),
  }))
}

interface GetMarketDetailHolidaysParams {
  marketId: string
  analystId?: string
  horizonMonths: number
}

export const getMarketDetailHolidays = async ({
  marketId,
  analystId,
  horizonMonths,
}: GetMarketDetailHolidaysParams) => {
  const today = new Date()
  const result = await arriereBackoffice.queries.analystHomeHolidayIndicators({
    __alias: 'getMarketDetailHolidays',
    __args: {
      input: {
        marketIds: [marketId],
        analystId,
        horizonDays: differenceInCalendarDays(addMonths(today, horizonMonths), today),
      },
    },
    holidays: { seasonName: true, seasonType: true },
    cells: {
      seasonName: true,
      startDate: true,
      endDate: true,
      occupancy: true,
      marketOccupancy: true,
      pickupRevenue7d: true,
      revenueVsTarget: true,
      status: true,
    },
  })

  return {
    holidays: result.holidays,
    cells: result.cells.map(cell => ({
      ...cell,
      startDate: formatDate(cell.startDate, SHORT_ISO_DATE_FORMAT),
      endDate: formatDate(cell.endDate, SHORT_ISO_DATE_FORMAT),
    })),
  }
}

interface GetMarketDetailTimelineParams {
  marketId: string
  analystId?: string
  limit: number
}

export const getMarketDetailTimeline = async ({
  marketId,
  analystId,
  limit,
}: GetMarketDetailTimelineParams) => {
  const result = await arriereBackoffice.queries.revenueManagement({
    __alias: 'getMarketDetailTimeline',
    changeLog: {
      __args: { marketId, limit },
      id: true,
      summary: true,
      sourceType: true,
      effectiveAt: true,
    },
    reviewedDecisions: {
      __args: {
        marketId,
        analystId,
        sortOrder: RevenueManagementAutomationSortOrder.desc,
        pagination: { page: 1, size: limit },
      },
      results: {
        id: true,
        parentDecisionId: true,
        status: true,
        summary: true,
        reviewedAt: true,
        property: { name: true },
      },
    },
  })

  return {
    changeLog: result.changeLog ?? [],
    decisions: result.reviewedDecisions?.results ?? [],
  }
}
